import React from 'react';
import { useContent } from '../../hooks/useContent';
import { Skeleton } from '../ui/skeleton';
import { AlertCircle, Globe } from 'lucide-react';
import { LegalMetadata } from '../legal/LegalMetadata';
import { SafeHtml } from '../security/SafeHtml';

/**
 * CMSContent — Render CMS-managed content from the Legal Content Engine.
 *
 * Props:
 *   contentKey        — platform_content key to resolve
 *   flow              — optional flow context
 *   country           — optional country code
 *   showTitle         — render title (default: true)
 *   showSummary       — render summary below title (default: false)
 *   showLegalMetadata — render Version · Effective · Updated · Locale
 *                       footer from Agreement Library metadata
 *                       (default: false)
 *   className         — wrapper className
 *   as                — wrapper element type (default: 'div')
 *
 * Behavior:
 *   - Shows loading skeleton while fetching
 *   - Shows explicit error UI on failure (NO silent fail)
 *   - Shows "Translated from English" badge if fallback language
 *   - Renders body via the canonical `SafeHtml` sanitised renderer
 */
export function CMSContent({
  contentKey,
  flow,
  country,
  showTitle = true,
  showSummary = false,
  showLegalMetadata = false,
  className = '',
  as: Wrapper = 'div',
}) {
  const { loading, error, content } = useContent(contentKey, { flow, country });

  if (loading) {
    return (
      <Wrapper className={className} data-testid={`cms-loading-${contentKey}`}>
        <Skeleton className="h-5 w-3/4 mb-3" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-5/6" />
      </Wrapper>
    );
  }

  if (error || !content) {
    return (
      <Wrapper
        className={`flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm ${className}`}
        data-testid={`cms-error-${contentKey}`}
      >
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Content unavailable: {contentKey}</span>
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className} data-testid={`cms-content-${contentKey}`}>
      {content.isFallbackLanguage && (
        <div
          className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2 px-2 py-1 bg-muted/50 rounded w-fit"
          data-testid={`cms-fallback-badge-${contentKey}`}
        >
          <Globe className="h-3 w-3" />
          <span>Translated from English</span>
        </div>
      )}

      {showTitle && content.title && (
        <h2
          className="text-lg font-semibold mb-3"
          data-testid={`cms-title-${contentKey}`}
        >
          {content.title}
        </h2>
      )}

      {showSummary && content.summary && (
        <p
          className="text-sm text-muted-foreground mb-3"
          data-testid={`cms-summary-${contentKey}`}
        >
          {content.summary}
        </p>
      )}

      <SafeHtml
        html={content.body}
        className="prose prose-sm max-w-none dark:prose-invert"
        testId={`cms-body-${contentKey}`}
      />

      {showLegalMetadata && <LegalMetadata content={content} />}
    </Wrapper>
  );
}
